import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import { arrayMove, SortEnd } from 'react-sortable-hoc';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import { CloseMenu } from '@webapp/store/ui/action-creators';
import { UpdateTracPageField } from '@webapp/store/tracpage/action-creators';

import { parseLink } from '@webapp/sdk/tracpage';

import { isValidUrl } from '@webapp/utils/validators';

import { TracPage } from '@services/tracpage/types/trac-page';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';

import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';

import NewLink from './new-link';
import { getMerchProductMask } from '@webapp/utils/merch/getMerchProductMask';
import SortableLinks, {
  LinkWithTitle,
} from '@webapp/controllers/artist/artist-settings-links/sortable-links';
import FeaturedItemContainer from '@webapp/controllers/artist/artist-settings-links/feature-item-container';

const getLinkFromArray = (links: TracPageLink[], order: number) =>
  links.find((link) => link.order === order);

const newLink: TracPageLink = {
  title: '',
  url: '',
  order: 0,
  type: 'url',
  schedule: false,
  highlight: false,
  start: '',
  end: '',
};

const getNewLinks = (
  links: LinkWithTitle[],
  order: number,
  newData: Partial<LinkWithTitle>
) =>
  links.map((link) => {
    if (link.order === order) {
      return {
        ...link,
        ...newData,
      };
    }
    return { ...newLink, ...link };
  });

interface ArtistSettingsLinksProps {
  className?: string;
  links: TracPageLink[];
  closeMenu: () => CloseMenu;
  updateLinks: (
    value: TracPageLink[]
  ) => UpdateTracPageField<TracPage, 'links'>;
  releases: Record<'id' | 'value' | 'imageUrl', string | undefined>[];
  merchList: Record<'id' | 'value' | 'imageUrl', string | undefined>[];
}

const mapStateToProps = (state: AppState) => ({
  releases:
    state.release.summary?.all?.list
      ?.filter((release) => release.isPublished)
      .map(({ id, title, cover }) => ({
        id,
        value: title,
        imageUrl: cover.url,
      })) || [],
  merchList: state.merch?.artistMerch
    ?.filter((merch) => merch.status === 'published')
    ?.map((merch) => ({
      id: merch.id,
      value: merch.title,
      imageUrl: getMerchProductMask(merch),
    })),
  links: state.tracpage.artistPage.links || [],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  updateLinks: (links: TracPageLink[]) =>
    dispatch(
      new UpdateTracPageField<TracPage, 'links'>('tracpage', 'links', links)
    ),
});

const ArtistSettingsLinks: FunctionComponent<ArtistSettingsLinksProps> = ({
  className,
  links,
  releases,
  merchList,
  updateLinks,
  closeMenu,
}) => {
  const [lastIdUpdated, setLastIdUpdated] = React.useState<
    number | undefined
  >();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [onKeyEnter, setOnKeyEnter] = React.useState<boolean>(false);
  const [showNewLink, setShowNewLink] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (lastIdUpdated !== undefined) {
      const handler = setTimeout(() => {
        const updated = getLinkFromArray(links, lastIdUpdated);
        if (updated && updated.url && isValidUrl(updated.url) && onKeyEnter) {
          setIsLoading(true);
          parseLink(updated.url)
            .then((resp) => {
              const updatedLinks = getNewLinks(links, updated.order, {
                ...resp,
                showTitle: true,
              });
              updateLinks(updatedLinks);
              setLastIdUpdated(undefined);
              setIsLoading(false);
              setOnKeyEnter(false);
            })
            .catch(() => {
              setIsLoading(false);
              setOnKeyEnter(false);
            });
        }
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [lastIdUpdated, links, updateLinks, onKeyEnter]);

  const handleChange = <T extends keyof TracPageLink>(
    i: number,
    key: T,
    value: TracPageLink[T]
  ) => {
    const updatedLinks = getNewLinks(links, i, { [key]: value });
    updateLinks(updatedLinks);

    if (key === 'url') {
      const updated = getLinkFromArray(links, i);
      if (updated) {
        setLastIdUpdated(updated.order);
      }
    }
  };

  const getNewOrder = (items: TracPageLink[]) => {
    return items.map((item, order) => {
      return {
        ...item,
        order,
      };
    });
  };

  const handleRemove = (i: number) => {
    const newLinks = links.filter((link) => link.order !== i);
    const ordered = getNewOrder(newLinks);
    updateLinks(ordered);
  };

  const updateLinksOrder = ({ oldIndex, newIndex }: SortEnd) => {
    const newLinks = arrayMove(links, oldIndex, newIndex);
    const ordered = getNewOrder(newLinks);
    updateLinks(ordered);
  };

  const handleAddLink = (tracLink: TracPageLink) => {
    const newTracPageLink = [{ ...newLink, ...tracLink }];
    updateLinks(
      newTracPageLink.concat(
        links.map((link, i) => ({
          ...link,
          order: (link.order || i) + 1,
        }))
      )
    );
    setShowNewLink(false);
  };

  return (
    <SectionWrapper className={className || ''}>
      {(!!links.length || !!merchList.length || !!releases.length) && (
        <Box noBorderBottom>
          <BoxHeader
            title={
              <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.TITLE" />
            }
            text={
              <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.FEATURED.TEXT" />
            }
          />

          <FeaturedItemContainer
            releases={releases}
            links={links}
            merchList={merchList}
          />
        </Box>
      )}
      <Box noBorderTop>
        <BoxHeader
          title={
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.TITLE" />
          }
          text={
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.TEXT" />
          }
        />
        {/* TODO: DEVTEAM-845 - Integrate new input field for new link */}
        {!showNewLink ? (
          <NewLinkInput className="mb-10" onClick={() => setShowNewLink(true)}>
            <Typography className="label">
              <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.NEW_LINK.LABEL" />
            </Typography>
          </NewLinkInput>
        ) : (
          <NewLink
            className="mb-10"
            onAdd={handleAddLink}
            onCancel={() => setShowNewLink(false)}
          />
        )}
        <SortableLinks
          links={links}
          handleChange={handleChange}
          handleRemove={handleRemove}
          lockAxis="y"
          useDragHandle
          helperClass="sorting"
          lockToContainerEdges={true}
          loaderData={{ isLoading, lastIdUpdated }}
          onSortEnd={updateLinksOrder}
          setOnKeyEnter={setOnKeyEnter}
          closeMenu={closeMenu}
        />
      </Box>
    </SectionWrapper>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistSettingsLinks)`
  .menu {
    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }
`);

const SectionWrapper = styled.div`
  display: grid;
  gap: 3px;
  margin-bottom: 80px;
`;

const NewLinkInput = styled.div`
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 4px;
  min-height: 48px;
  padding: 16px 12px 16px 9px;
  cursor: pointer;

  .label {
    font-size: ${({ theme }) => theme.typography.span};
    font-weight: 700;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.secondaryDark};

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      font-size: ${({ theme }) => theme.typography.h4};
    }
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    min-height: 60px;
    padding: 22px 18px 22px 15px;
  }
`;
